import { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown, Loader2, Plus, Search } from "lucide-react";
import { cn } from "@/lib/utils";
import type { StatusOption } from "@/lib/api-types";

/**
 * Unified Status control (Frame.io parity). A searchable dropdown: pick an
 * existing status or type a new one and "Add" it. Presentational — the caller
 * wires options + mutations (member on the card / watch, guest on the share).
 *
 * Built as a self-contained popover (not a Radix menu) so the search input
 * isn't hijacked by menu typeahead. Closes on outside click / Escape.
 */
export type StatusControlProps = {
  options: StatusOption[];
  current: StatusOption | null;
  onPick: (optionId: string) => void;
  onCreate: (label: string) => void;
  disabled?: boolean;
  pending?: boolean;
  /** Allow typing a brand-new status (guests + members = true here). */
  canCreate?: boolean;
  align?: "start" | "end";
  className?: string;
};

export function StatusControl({
  options,
  current,
  onPick,
  onCreate,
  disabled,
  pending,
  canCreate = true,
  align = "start",
  className,
}: StatusControlProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const q = query.trim();
  const qLower = q.toLowerCase();
  const filtered = useMemo(
    () => options.filter((o) => o.label.toLowerCase().includes(qLower)),
    [options, qLower],
  );
  const exact = options.some((o) => o.label.toLowerCase() === qLower);
  const showCreate = canCreate && q.length > 0 && !exact;

  function commit(fn: () => void) {
    fn();
    setOpen(false);
    setQuery("");
  }

  return (
    <div ref={rootRef} className={cn("relative", className)}>
      <button
        type="button"
        disabled={disabled}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        className="flex w-full items-center gap-2 rounded-lg border border-border bg-background px-2.5 py-1.5 text-xs text-foreground transition-colors hover:bg-secondary/60 disabled:opacity-60"
      >
        {current ? (
          <>
            <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: current.color }} />
            <span className="flex-1 truncate text-left">{current.label}</span>
          </>
        ) : (
          <span className="flex-1 truncate text-left text-muted-foreground">No status</span>
        )}
        {pending ? (
          <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-muted-foreground" />
        ) : (
          <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" strokeWidth={1.75} />
        )}
      </button>

      {open && (
        <div
          onClick={(e) => e.stopPropagation()}
          className={cn(
            "absolute z-50 mt-1 w-56 overflow-hidden rounded-lg border border-border bg-popover text-popover-foreground shadow-lg",
            align === "end" ? "right-0" : "left-0",
          )}
        >
          <div className="border-b border-border p-1.5">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    if (filtered.length > 0) commit(() => onPick(filtered[0].id));
                    else if (showCreate) commit(() => onCreate(q));
                  }
                }}
                placeholder="Search or add…"
                className="w-full rounded-md border border-border bg-background py-1.5 pl-7 pr-2 text-xs text-foreground outline-none placeholder:text-muted-foreground focus:border-ring"
              />
            </div>
          </div>

          <div className="max-h-56 overflow-y-auto p-1">
            {filtered.map((o) => (
              <button
                key={o.id}
                type="button"
                onClick={() => commit(() => onPick(o.id))}
                className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs text-foreground transition-colors hover:bg-secondary"
              >
                <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: o.color }} />
                <span className="flex-1 truncate">{o.label}</span>
                {current?.id === o.id && <Check className="h-3.5 w-3.5 shrink-0 text-primary" />}
              </button>
            ))}

            {showCreate && (
              <button
                type="button"
                onClick={() => commit(() => onCreate(q))}
                className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs text-primary transition-colors hover:bg-primary/10"
              >
                <Plus className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">Add “{q}”</span>
              </button>
            )}

            {filtered.length === 0 && !showCreate && (
              <div className="px-2 py-3 text-center text-xs text-muted-foreground">No statuses</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
